import { EVENTBRITE_CATEGORY_TO_REFINEMENT } from '@eventbrite/categories';
import { setWindowLocation } from '@eventbrite/http';
import { GenericLazyString } from '@eventbrite/i18n';
import loadable from '@loadable/component';
import React from 'react';
import {
    ARTICLE_TEMPLATE,
    CARD_BIG,
    CARD_TEMPLATE_4,
    CARD_TEMPLATE_8,
    FEATURED_TEMPLATE,
    LARGE_CARD_TEMPLATE_3,
    LARGE_CARD_TEMPLATE_6,
    LIST_CARD_TEMPLATE,
} from '../../constants';
import { GA_CITY_BROWSE_CATEGORY } from '../../constants/analytics';
import { Event } from '../../types';
import { FeatureFlags, ShareOptions } from '../../types/index';
import { BucketHeader } from '../BucketHeader';

type TrackClickIdUrl = { id: string; url?: string };
type TrackClickFn = (
    identifer: TrackClickIdUrl,
    gaAction: string,
    organizerFollowerCount?: number,
) => void;

const ArticleCardGroup = loadable(() => import('./ArticleCardGroup'), {
    resolveComponent: (components: any) => components.ArticleCardGroup,
});
const FeaturedArticleCardGroup = loadable(() => import('./ArticleCardGroup'), {
    resolveComponent: (components: any) => components.FeaturedArticleCardGroup,
});
const BigEventCardGroup = loadable(() => import('./BigEventCardGroup'), {
    resolveComponent: (components: any) => components.BigEventCardGroup,
});
const SmallEventCardGroup = loadable(() => import('./SmallEventCardGroup'), {
    resolveComponent: (components: any) => components.SmallEventCardGroup,
});
const ListEventCardGroup = loadable(() => import('./ListEventCardGroup'), {
    resolveComponent: (components: any) => components.ListEventCardGroup,
});
const LoadingCards = loadable(() => import('../LoadingCards'), {
    resolveComponent: (components: any) => components.LoadingCards,
});

const CARD_GROUP_MAP = {
    [ARTICLE_TEMPLATE]: ArticleCardGroup,
    [FEATURED_TEMPLATE]: FeaturedArticleCardGroup,
    [LARGE_CARD_TEMPLATE_3]: BigEventCardGroup,
    [LARGE_CARD_TEMPLATE_6]: BigEventCardGroup,
    [LIST_CARD_TEMPLATE]: ListEventCardGroup,
    [CARD_TEMPLATE_4]: SmallEventCardGroup,
    [CARD_TEMPLATE_8]: SmallEventCardGroup,
    [CARD_BIG]: BigEventCardGroup,
};

interface CardBucketProps {
    /*
     * Array of preformatted objects that will represent that cards within the bucket
     */
    content: any[];
    /*
     * The manner in which this CardBucket should display its content
     */
    template: string;
    /*
     * The name of the given bucket ie. "Upcoming Music Events"
     */
    name: GenericLazyString;
    /*
     * The url clicking "See More" should connect the user to
     */
    linkUrl?: string;
    /*
     * Is user authenticated?
     */
    isAuthenticated?: boolean;
    /*
     * The key of the associated bucket
     */
    bucketKey?: string;
    /*
     * Tracking callback for event clicks
     */
    onTrackEventClickInternal?: Function;
    /*
     * Track GA Events from state
     */
    trackGAEvent?: Function;
    /*
     * Track GA Events from state
     */
    trackGAEventOnPageUnload?: Function;
    isLoading?: boolean;
    featureFlags: FeatureFlags;
    trackSeeMore?: Function;
    experiments?: any;
    shareOptions?: ShareOptions;
    affCode?: string;
    keyMin?: string;
    locale?: string;
    section?: string;
    seeMore?: {
        url?: string;
        params?: { dates?: string[]; places?: string[]; tags?: string[] };
    };
    locationSlug?: string;
}

export const CardBucket: React.FunctionComponent<CardBucketProps> = (props) => {
    const {
        content,
        name,
        template,
        linkUrl,
        isAuthenticated,
        experiments,
        featureFlags,
        trackGAEvent,
        trackGAEventOnPageUnload,
        bucketKey,
        isLoading,
        trackSeeMore,
        onTrackEventClickInternal,
        affCode,
        keyMin,
        locationSlug,
        seeMore,
        locale,
        section,
    } = props;

    let seeMoreLink = linkUrl;
    const DynamicCardGroup = (CARD_GROUP_MAP as any)[template];
    const enableClientSideLinking =
        keyMin === 'ccs' && featureFlags.indexCategoryBrowse;

    if (enableClientSideLinking && seeMore?.params?.tags?.[0]) {
        const categoryId = seeMore?.params?.tags?.[0] || '';
        const categoryRefinement =
            EVENTBRITE_CATEGORY_TO_REFINEMENT[categoryId];
        seeMoreLink = `/b/${locationSlug}/${categoryRefinement}/`;
    }

    const isSmallCity = template === CARD_BIG;

    const handleTrackClick = React.useCallback<TrackClickFn>(
        ({ url, id }, action, organizerFollowerCount) => {
            if (bucketKey) {
                trackGAEvent?.({
                    category: section ? section : GA_CITY_BROWSE_CATEGORY,
                    action,
                    label: bucketKey,
                    dimensions: {
                        eventId: id,
                    },
                    metrics: {
                        organizerFollowerCount: organizerFollowerCount || 0,
                    },
                });
            }

            /* Need to fire tracking action on the seemore, so necessary to
            manually set windowlocation rather that relying on the link. */
            if (url) {
                setWindowLocation(url);
            }
        },
        [bucketKey, trackGAEvent, section],
    );

    const handleTrackClickInternal = React.useCallback(
        (index, id, ticketsBy) => {
            const trackingData = {
                clickRank: index,
                navigation: `city_browse.${bucketKey}`,
                itemId: id,
            };

            onTrackEventClickInternal &&
                onTrackEventClickInternal(trackingData, ticketsBy);
        },
        [bucketKey, onTrackEventClickInternal],
    );

    const renderPromotedResults = content.some(
        (contentEvent: Event) =>
            contentEvent.promotedListingMetadata != undefined,
    );

    return (
        <div>
            <BucketHeader
                title={name}
                trackSeeMore={trackSeeMore}
                linkUrl={seeMoreLink}
                template={template}
                trackGAEvent={trackGAEvent}
                bucketKey={bucketKey}
                enableClientSideLinking={enableClientSideLinking}
            />
            {isLoading ? (
                <section className="eds-l-pad-left-6">
                    <LoadingCards cardCount={3} />
                </section>
            ) : (
                <DynamicCardGroup
                    template={template}
                    cards={content}
                    linkUrl={seeMoreLink}
                    isAuthenticated={isAuthenticated}
                    onTrackClick={handleTrackClick}
                    onTrackClickInternal={handleTrackClickInternal}
                    trackGAEvent={trackGAEvent}
                    trackGAEventOnPageUnload={trackGAEventOnPageUnload}
                    isSmallCity={isSmallCity}
                    title={name}
                    experiments={experiments}
                    featureFlags={featureFlags}
                    affCode={affCode}
                    enableClientSideLinking={enableClientSideLinking}
                    renderPromotedResults={renderPromotedResults}
                    locale={locale}
                />
            )}
        </div>
    );
};

export default CardBucket;
